import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { type UpdateCppRateCardBody, updateCppRateCard } from '~/services/LuxLoyalty';

import { costPerPointQueryKeys } from './costPerPointQueryKeys';

interface UpdateCppRateCardParams {
  id: string;
  body: UpdateCppRateCardBody;
  /**
   * When `false`, suppresses the "Rate card updated successfully" toast.
   * Used during a save-and-activate flow so only the activation outcome (success or
   * error) is surfaced to the user, instead of a misleading success toast followed
   * by an activation error toast. Defaults to showing the toast (`true`).
   */
  showSuccessToast?: boolean;
  /**
   * When `false`, suppresses the "Error updating rate card" toast.
   * Used by the rate card dialog, which surfaces mutation errors inline (MUI Alert)
   * instead of via snackbar. Defaults to showing the toast (`true`).
   */
  showErrorToast?: boolean;
}

/** Updates a CPP rate card (state-aware on the server) and invalidates the list/detail caches. */
export function useUpdateCppRateCard() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ id, body }: UpdateCppRateCardParams) => {
      return await updateCppRateCard(id, body);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: costPerPointQueryKeys.all });
      // Both toast flags default to true (undefined → shown); normalise to a concrete boolean.
      if (variables.showSuccessToast ?? true) {
        enqueueSnackbar('Rate card updated successfully', { variant: 'success' });
      }
    },
    onError: (error: Error, variables) => {
      if (variables?.showErrorToast ?? true) {
        enqueueSnackbar(`Error updating rate card: ${error.message || 'Unknown error'}`, { variant: 'error' });
      }
    },
  });
}
